// src/dataTypes/statisticsV2Managment/audienceMap.ts
//
// Points de la carte d'audience du Dashboard, tirés des statistiques V2 (sessions de
// visionnage structurées) et non plus de CustomerFrontEvent, qui ne reçoit plus rien
// depuis le 2026-08-07.

import { Coordinate } from '../geolocationManagment/types';
import { fetchLiveStats, fetchLiveHistogram, fetchVideoStats, fetchVideoHistogram,
         Histogram, ViewerLocation } from '../../backendServices/statisticsV2Managment/api';

export interface AudienceMapPoints {
  live  : Coordinate[];
  replay: Coordinate[];
}

// Un histogramme par diffusion / vidéo : on borne le nombre d'appels, les plus récentes d'abord.
const MAX_RUNS_PER_KIND = 12;

type Run = { id: number; start: number; end: number };

const overlapping = (runs: Run[], from: Date, to: Date): Run[] =>
  runs
    .filter(r => r.start <= to.getTime() && r.end >= from.getTime())
    .sort((a, b) => b.start - a.start)
    .slice(0, MAX_RUNS_PER_KIND);

/**
 * Spectateurs par ville dans la fenêtre [from, to] pour un histogramme : pour chaque ville, le
 * maximum de ses spectateurs distincts sur les tranches de la fenêtre (additionner les tranches
 * compterait plusieurs fois les mêmes personnes). Sans détail par tranche, repli sur toute la durée.
 */
const locationsInWindow = (h: Histogram, from: Date, to: Date): ViewerLocation[] => {
  const withDetail = h.buckets.some(b => b.locations !== undefined);
  if (!withDetail) return h.locations;

  const byCity = new Map<string, ViewerLocation>();
  for (const b of h.buckets) {
    const start = new Date(b.start).getTime();
    const end   = start + h.bucketSeconds * 1000;
    if (end < from.getTime() || start > to.getTime()) continue;
    for (const l of b.locations ?? []) {
      const k = `${l.lat}|${l.lng}`;
      const prev = byCity.get(k);
      if (!prev || l.viewers > prev.viewers) byCity.set(k, l);
    }
  }
  return Array.from(byCity.values());
};

const toCoordinates = (histograms: Histogram[], from: Date, to: Date, color: string): Coordinate[] => {
  const byCity = new Map<string, { loc: ViewerLocation; viewers: number }>();
  for (const h of histograms) {
    for (const l of locationsInWindow(h, from, to)) {
      const k = `${l.lat}|${l.lng}`;
      const e = byCity.get(k);
      if (e) e.viewers += l.viewers; else byCity.set(k, { loc: l, viewers: l.viewers });
    }
  }
  return Array.from(byCity.values()).map(({ loc, viewers }) => ({
    lat  : loc.lat,
    lng  : loc.lng,
    info1: [loc.city, loc.country].filter(Boolean).join(', ') || 'Lieu inconnu',
    info2: `${viewers} spectateur${viewers > 1 ? 's' : ''}`,
    color,
  }));
};

export const fetchAudienceMapPoints = async (adminId: number, from: Date, to: Date): Promise<AudienceMapPoints> => {
  const [ lives, videos ] = await Promise.all([ fetchLiveStats(adminId), fetchVideoStats(adminId) ]);
  const now = Date.now();

  const liveRuns  = overlapping(lives.map(l => ({ id: l.id, start: new Date(l.startDate).getTime(), end: l.endDate ? new Date(l.endDate).getTime() : now })), from, to);
  const videoRuns = overlapping(videos.map(v => ({ id: v.id, start: new Date(v.startDate).getTime(), end: new Date(v.endDate).getTime() })), from, to);

  const [ liveHistos, videoHistos ] = await Promise.all([
    Promise.all(liveRuns.map(r  => fetchLiveHistogram(adminId, r.id))),
    Promise.all(videoRuns.map(r => fetchVideoHistogram(adminId, r.id))),
  ]);

  return {
    live  : toCoordinates(liveHistos,  from, to, 'var(--color-Graph-Live)'),
    replay: toCoordinates(videoHistos, from, to, 'var(--color-Graph-Replay)'),
  };
};
